import { Vue, Component, Provide, Prop } from 'vue-property-decorator';
import GoogleMaterialIcon from '@/components/icons/google-material-icon/google-material-icon';
import { rolesAndPromissionAPI } from '@/providers/apis/roles-and-permission/roles-and-permission';
import { profileAPI } from '@/providers/apis/profile/profile';
import { isAPICatchError, isEmptyValue, sortByProperty } from '@/utils/utils';
import { RoleModel } from '@/models/role-permission/role-permission';
import { groupsApi } from '@/providers/apis/groups/groups';
import { copy } from '@/utils/cohort';
import AclSelect from '@/components/acl-select/acl-select';

@Component({
    name: 'add-user',
    components: {
        'material-icons': GoogleMaterialIcon,
        'acl-select': AclSelect,
    },
})

export default class AddUser extends Vue {

    // -----------------------------------------------------------------------------
    // Hooks
    @Provide('validator') public $validator: any = this.$validator;

    @Prop()
    private userData: any;

    private addUser: any = {
        first_name: '',
        last_name: '',
        username: '',
        email: '',
        roles: [],
        designation: '',
    };

    private displayErrorMessage: boolean = false;

    private errorMessage: string = '';

    private rolesOptions: any = [];

    private teacherAdmin: any = {};

    private isSubmitted: boolean = false;

    private get isTeacherAdmin() {
        return this.addUser.roles.includes(this.teacherAdmin.id);
    }

    private userForm: any = {
        first_name: {
            name: 'First name',
            field: 'b-form-input',
            modelKey: 'first_name',
            modelValue: '',
            validate: { required: true },
            eventBind: {},
        },
        last_name: {
            name: 'Last name',
            field: 'b-form-input',
            modelKey: 'last_name',
            modelValue: '',
            validate: { required: true },
            eventBind: {},
        },
        username: {
            name: 'Username',
            field: 'b-form-input',
            modelKey: 'username',
            modelValue: '',
            validate: { required: true },
            eventBind: {},
        },
        email: {
            name: 'Email',
            field: 'b-form-input',
            modelKey: 'email',
            modelValue: '',
            validate: { required: true },
            eventBind: {},
        },
        designation: {
            name: 'Designation',
            field: 'b-form-input',
            modelKey: 'designation',
            modelValue: '',
            validate: { required: true },
            eventBind: {},
        },
        roles: {
            name: 'Roles',
            field: 'b-form-select',
            modelKey: 'roles',
            modelValue: '',
            validate: { required: true },
            eventBind: {},
            propBind: {
                'options': this.rolesOptions,
                // multiple: true,
                'text-field': 'displayName',
                'value-field': 'id',
            },
        },
    };

    private get enableHierarchy() {
        if (this.userData && this.userData.group_school_id) {
            return true;
        } else {
            return this.isEnableSchool && this.isSubmitted;
        }
    }

    private selectionALCsList: any = {};

    private selectionALCs: any = {};

    private cohortLevel: any = [];

    private get isDropDownValidation() {
        if (this.isSubmitted && this.addUser.roles.length === 0) {
            return false;
        }
    }

    private isLoading: boolean = false;

    private isEnableSchool: boolean = true;

    private preSelectedSchool: any = {};

    // Hooks
    public created() {
        if (this.userData && this.userData.id) {
            this.addUser.id = this.userData.id;
            this.addUser.first_name = this.userData.firstName;
            this.addUser.last_name = this.userData.lastName;
            this.addUser.username = this.userData.userName;
            this.addUser.email = this.userData.email;
            this.addUser.roles = this.userData.roles;
            this.addUser.designation = this.userData.designation;
            if (this.userData && this.userData.group_school_id) {
                this.addUser.group_school_id = this.userData.group_school_id;
            }
            this.isEnableSchool = false;
        }
        this.fetchUserACL();
        this.fetchRoleList();
    }

    // -----------------------------------------------------------------------------
    // Actions
    public onSwitchComponent(key: string) {
        this.$emit('onSwitchComponent', key);
    }

    private createUser() {
        const that = this;
        this.isSubmitted = true;
        const key: string = 'cohort-access';
        let groupId = '';
        if (this.enableHierarchy) {
            groupId = this.selectionALCs[this.cohortLevel.length];
        } else {
            groupId = this.selectionALCs[this.cohortLevel.length] || this.addUser.group_school_id;
        }
        if (!isEmptyValue(groupId)) {
            this.addUser.group_school_id = groupId;
        }
        this.$validator.validateAll().then((valid: boolean) => {
            if (this.isTeacherAdmin) {
                if (isEmptyValue(this.addUser.group_school_id)) {
                   return;
                }
            }
            if (valid) {
                const payloadObj = {
                    payload: this.addUser,
                };
                this.displayErrorMessage = false;
                const apiCall = this.userData && this.userData.id ? rolesAndPromissionAPI.updateUser(payloadObj) :
                    rolesAndPromissionAPI.createUser(payloadObj);
                that.isLoading = true;
                apiCall.then((data: any) => {
                    that.isLoading = false;
                    if (data && isAPICatchError(data)) {
                        this.errorMessage = data.displayMessage && data.displayMessage.message ?
                            data.displayMessage.message : '';
                        this.displayErrorMessage = true;
                    } else {
                        if (this.isTeacherAdmin) {
                            this.$emit('onSwitchComponent', 'user-list');
                        } else {
                            const params = {
                                userId: data,
                            };
                            if (this.userData && this.userData.id) {
                                params.userId = this.userData.id;
                            }
                            that.isLoading = true;
                            profileAPI.fetchUserDetails(params).then((userDetails: any) => {
                                for (const iterator of userDetails.roles) {
                                    const userRole = that.rolesOptions.find((item: RoleModel) => item.id === iterator);
                                    if (userRole) {
                                        userDetails.rolesName.push(userRole.displayName);
                                    }
                                }
                                that.isLoading = false;
                                that.$emit('onSwitchComponent', key, userDetails);
                            });
                        }
                    }
                });
            }
        });
    }

    private fetchRoleList() {
        const roles = this.$store.state.rosterStore.rolesList;
        this.rolesOptions = roles;
        this.userForm.roles.propBind.options = roles;
        this.teacherAdmin = this.rolesOptions.find((item: any) => item.code === 'TEACHER_ADMIN');
    }

    private inputValidator(errors: any, keyName: string) {
        return this.isSubmitted && errors.has(keyName);
    }

    private async fetchUserACL() {
        this.isLoading = true;
        const hierarchy: any = await groupsApi.fetchUserHierarchies();
        const tenantSelect = hierarchy.find((item: any) => {
            return item.tenants.find((tenantItem: any) => {
                    return tenantItem.id === this.$access.getTenantId();
                });
            });
        sortByProperty(tenantSelect.levels, 'sequence', 'ASC');
        const classSeq = tenantSelect.levels.find((item: any) => {
            return item.type === 'class';
        });
        if (classSeq && classSeq.sequence) {
            tenantSelect.levels = tenantSelect.levels.filter((item: any) => {
                return item.sequence < classSeq.sequence;
            });
        }
        this.isLoading = false;
        this.cohortLevel = tenantSelect.levels;
        this.fetchCohortListAll();
    }

    private async fetchCohortListAll() {
        const that = this;
        for await (const [index, iterator] of that.cohortLevel.entries()) {
            iterator.child = {};
            iterator.child = await groupsApi.fetchGroupedHierarchyMapping({ type: iterator.type });
            that.selectionALCs[iterator.sequence] = '';
            if (iterator.sequence === 1) {
                that.selectionALCsList[iterator.sequence] = iterator.child[0].cohortList;
                that.selectionALCs[iterator.sequence] = 0;
            }
            if (index === that.cohortLevel.length - 1) {
                that.cohortLevel = that.cohortLevel;
                if (that.userData && that.userData.id && that.userData.group_school_id) {
                    that.findPreSelectedSchool();
                }
            }
        }
    }

    private onSelectListItem(type: any, selectedId: any, index: any) {
        const that = this;
        const selected = that.cohortLevel[index + 1];
        if (selected && selected.sequence) {
            for (let listIndex = index; listIndex < that.cohortLevel.length; listIndex++) {
                that.selectionALCs[listIndex + 1] = '';
                that.$set(this.selectionALCsList, selected.sequence, []);
            }
        }
        that.$set(that.selectionALCs, that.cohortLevel[index].sequence, selectedId);
        if (selected && selected.sequence && selected.child[selectedId]) {
            that.$set(that.selectionALCsList, selected.sequence, selected.child[selectedId].cohortList);
        }
    }

    private goTocohortPage() {
        const key: string = 'cohort-access';
        this.$emit('onSwitchComponent', key, this.userData);
    }

    private findPreSelectedSchool() {
        const that = this;
        const schoolHierarchy = that.cohortLevel.find((item: any) => item.type === 'school');
        let emptyObj: any = {};
        for (let index = that.cohortLevel.length; index >= 0; index--) {
            const element = that.cohortLevel[index];
            if (index === that.cohortLevel.length) {
                const keysParent = Object.keys(schoolHierarchy.child).map(Number);
                for (const iteratorParent of keysParent) {
                    const cohortList = schoolHierarchy.child[iteratorParent].cohortList;
                    for (const iterator of cohortList) {
                        if (iterator.id === that.userData.group_school_id) {
                            emptyObj = iterator;
                            that.selectionALCs[schoolHierarchy.sequence] = iterator.id;
                            this.selectionALCsList[schoolHierarchy.sequence] = cohortList;
                        }
                    }
                }
            } else {
                const parentId = emptyObj.parentId;
                const keysParent = Object.keys(element.child).map(Number);
                for (const iteratorParent of keysParent) {
                    const cohortList = element.child[iteratorParent].cohortList;
                    for (const iterator of cohortList) {
                        if (iterator.id === parentId) {
                            that.selectionALCs[element.sequence] = iterator.id;
                            this.selectionALCsList[element.sequence] = cohortList;
                            emptyObj = iterator;
                        }
                    }
                }
            }
        }
    }
}
